import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import DeleteHoldButton from '../components/common/DeleteHoldButton';
import { usePlatformContentEditorAccess } from '../hooks/usePlatformContentEditorAccess';
import { isLearningPlanBuiltinCategory, type LearningPlanActivity, type LearningPlanCategory } from '../types/learningPlanActivity';
import {
  loadLearningPlanActivities,
  loadLearningPlanCategories,
  resetLearningPlanActivities,
  saveLearningPlanActivities,
  saveLearningPlanCategories,
} from '../utils/learningPlanActivityStore';
import './AdminLearningPlanActivityPage.css';
import { LearningPlanGuideView } from '../components/program/LearningPlanGuideView';

function newId(prefix: string): string {
  return `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;
}

type ActivityDraft = {
  id: string;
  name: string;
  method: string;
  guide: string;
  isNew: boolean;
};

type PageConfirm = {
  title: string;
  description: string;
  confirmLabel: string;
  run: () => void;
};

/** 5열 × 3행. 첫 페이지는 추가 칸 하나를 뺀다. */
const LEARNING_PLAN_PAGE1_SIZE = 14;
const LEARNING_PLAN_PAGE_SIZE = 15;

const AdminLearningPlanActivityPage: React.FC = () => {
  const { ready: accessReady, allowed: canAccess } = usePlatformContentEditorAccess();
  const [categories, setCategories] = useState<LearningPlanCategory[]>(() => loadLearningPlanCategories());
  const [rows, setRows] = useState<LearningPlanActivity[]>(() => loadLearningPlanActivities());
  const [phase, setPhase] = useState('intro');
  const [page, setPage] = useState(1);
  const [draft, setDraft] = useState<ActivityDraft | null>(null);
  const [categoryEditor, setCategoryEditor] = useState<{ id: string | null; name: string } | null>(null);
  const [categoryError, setCategoryError] = useState('');
  const [pageConfirm, setPageConfirm] = useState<PageConfirm | null>(null);

  const visible = useMemo(
    () => rows.filter((row) => row.phase === phase).sort((a, b) => a.sort - b.sort || a.name.localeCompare(b.name, 'ko')),
    [phase, rows]
  );

  useEffect(() => {
    if (categories.some((row) => row.id === phase)) return;
    setPhase(categories[0]?.id ?? 'intro');
  }, [categories, phase]);

  const totalPages =
    visible.length <= LEARNING_PLAN_PAGE1_SIZE
      ? 1
      : 1 + Math.ceil((visible.length - LEARNING_PLAN_PAGE1_SIZE) / LEARNING_PLAN_PAGE_SIZE);
  const safePage = Math.min(Math.max(1, page), totalPages);
  const safePageSize = safePage <= 1 ? LEARNING_PLAN_PAGE1_SIZE : LEARNING_PLAN_PAGE_SIZE;
  const safePageStart = safePage <= 1 ? 0 : LEARNING_PLAN_PAGE1_SIZE + (safePage - 2) * LEARNING_PLAN_PAGE_SIZE;
  const pageRows = visible.slice(safePageStart, safePageStart + safePageSize);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const selectCategory = (id: string) => {
    setPhase(id);
    setPage(1);
  };

  const commitRows = (next: LearningPlanActivity[]) => {
    setRows(next);
    saveLearningPlanActivities(next);
  };

  const commitCategories = (next: LearningPlanCategory[]) => {
    setCategories(next);
    saveLearningPlanCategories(next);
  };

  const openNew = () => {
    setDraft({ id: newId('act'), name: '', method: '', guide: '', isNew: true });
  };

  const saveDraft = () => {
    if (!draft) return;
    if (draft.isNew) {
      const sort = visible.reduce((max, row) => Math.max(max, row.sort), -1) + 1;
      commitRows([...rows, { id: draft.id, phase, name: draft.name.trim(), method: draft.method.trim(), guide: draft.guide.trim(), sort }]);
    } else {
      commitRows(
        rows.map((row) =>
          row.id === draft.id ? { ...row, name: draft.name.trim(), method: draft.method.trim(), guide: draft.guide.trim() } : row
        )
      );
    }
    setDraft(null);
  };

  const removeDraft = () => {
    if (!draft || draft.isNew) {
      setDraft(null);
      return;
    }
    commitRows(rows.filter((row) => row.id !== draft.id));
    setDraft(null);
  };

  const saveCategory = () => {
    if (!categoryEditor) return;
    const name = categoryEditor.name.trim();
    if (!name) {
      setCategoryError('이름을 적어 주세요.');
      return;
    }
    if (categoryEditor.id) {
      commitCategories(categories.map((row) => (row.id === categoryEditor.id ? { ...row, name } : row)));
    } else {
      const id = newId('cat');
      const sort = categories.reduce((max, row) => Math.max(max, row.sort), -1) + 1;
      commitCategories([...categories, { id, name, sort }]);
      setPhase(id);
    }
    setCategoryEditor(null);
    setCategoryError('');
  };

  const removeCategory = (category: LearningPlanCategory) => {
    if (isLearningPlanBuiltinCategory(category.id)) return;
    setPageConfirm({
      title: `「${category.name}」 카테고리와 그 안의 활동을 삭제할까요?`,
      description: '삭제하면 되돌릴 수 없습니다.',
      confirmLabel: '삭제',
      run: () => {
        commitCategories(categories.filter((row) => row.id !== category.id));
        commitRows(rows.filter((row) => row.phase !== category.id));
        if (phase === category.id) setPhase('intro');
      },
    });
  };

  if (!accessReady) {
    return (
      <section className="cp-admin-notices-page" aria-labelledby="cp-learning-plan-admin-title">
        <h2 id="cp-learning-plan-admin-title" className="cp-page-title">
          활동계획 활동 관리
        </h2>
        <p className="cp-dashboard-placeholder" role="status">
          확인 중…
        </p>
      </section>
    );
  }

  if (!canAccess) {
    return (
      <section className="cp-admin-notices-page" aria-labelledby="cp-learning-plan-admin-title">
        <h2 id="cp-learning-plan-admin-title" className="cp-page-title">
          활동계획 활동 관리
        </h2>
        <p className="cp-dashboard-placeholder">이 페이지를 볼 수 있는 권한이 없습니다.</p>
      </section>
    );
  }

  return (
    <div className="cp-daily-note-page cp-learning-plan-admin">
      <header className="cp-daily-note-header">
        <div className="cp-daily-note-header-text">
          <h1 id="cp-learning-plan-admin-title" className="cp-daily-note-title">
            활동계획 활동 관리
          </h1>
          <p className="cp-daily-note-desc">
            카테고리별로 활동을 적어 둡니다. 카드를 누르면 활동명, 활동 방법, 활동 설명을 수정합니다.
          </p>
        </div>
        <button
          type="button"
          className="cp-learning-plan-admin-reset"
          onClick={() => {
            setPageConfirm({
              title: '적어 둔 카테고리와 활동을 기본 목록으로 되돌릴까요?',
              description: '직접 만든 카테고리와 활동은 사라집니다.',
              confirmLabel: '되돌리기',
              run: () => {
                setRows(resetLearningPlanActivities());
                setCategories(loadLearningPlanCategories());
                setPhase('intro');
                setPage(1);
              },
            });
          }}
        >
          기본 목록으로
        </button>
      </header>
      <div className="cp-daily-note-chips-wrap">
        <div className="cp-todo-note-chips" role="tablist" aria-label="활동 카테고리">
          {categories.map((category) => (
            <div key={category.id} className={`cp-todo-note-chip-row${phase === category.id ? ' is-active' : ''}`}>
              <button
                type="button"
                role="tab"
                aria-selected={phase === category.id}
                className="cp-todo-note-chip-main"
                onClick={() => selectCategory(category.id)}
              >
                {category.name}
                <span className="cp-learning-plan-admin-count">{rows.filter((row) => row.phase === category.id).length}</span>
              </button>
              <span className="cp-todo-note-chip-icon-actions">
                <button
                  type="button"
                  className="cp-todo-note-chip-icon-btn"
                  aria-label={`${category.name} 이름 수정`}
                  onClick={() => {
                    setCategoryError('');
                    setCategoryEditor({ id: category.id, name: category.name });
                  }}
                >
                  <img src={`${process.env.PUBLIC_URL || ''}/edit.svg`} alt="" className="cp-todo-note-chip-icon" />
                </button>
                {isLearningPlanBuiltinCategory(category.id) ? null : (
                  <button
                    type="button"
                    className="cp-todo-note-chip-icon-btn"
                    aria-label={`${category.name} 삭제`}
                    onClick={() => removeCategory(category)}
                  >
                    <img src={`${process.env.PUBLIC_URL || ''}/Trash.svg`} alt="" className="cp-todo-note-chip-icon" />
                  </button>
                )}
              </span>
            </div>
          ))}
          <button
            type="button"
            className="cp-todo-note-chip cp-todo-note-chip-add"
            aria-label="카테고리 추가"
            onClick={() => {
              setCategoryError('');
              setCategoryEditor({ id: null, name: '' });
            }}
          >
            +
          </button>
        </div>
      </div>
      <div className="cp-daily-note-scroll">
        <div className="cp-daily-note-grid">
          {safePage === 1 ? (
            <button type="button" className="cp-daily-note-add-card" onClick={openNew} aria-label="활동 추가">
              <span className="cp-daily-note-add-circle" aria-hidden>
                <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
                  <circle cx="24" cy="24" r="23" stroke="currentColor" strokeWidth="2" strokeDasharray="4 4" fill="none" />
                  <path d="M24 14V34M14 24H34" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
                </svg>
              </span>
            </button>
          ) : null}
          {pageRows.map((row) => (
            <button
              key={row.id}
              type="button"
              className="cp-daily-note-card"
              onClick={() => setDraft({ id: row.id, name: row.name, method: row.method, guide: row.guide, isNew: false })}
            >
              <div className="cp-learning-plan-card-titles">
                <span
                  className={
                    row.name.trim()
                      ? 'cp-daily-note-card-date cp-learning-plan-card-name'
                      : 'cp-daily-note-card-date cp-learning-plan-card-name is-empty'
                  }
                >
                  {row.name.trim() || '활동명'}
                </span>
                <span
                  className={
                    row.method.trim()
                      ? 'cp-daily-note-card-date cp-learning-plan-card-method'
                      : 'cp-daily-note-card-date cp-learning-plan-card-method is-empty'
                  }
                >
                  {row.method.trim() || '활동 방법'}
                </span>
              </div>
              <div className={row.guide.trim() ? 'cp-daily-note-card-body' : 'cp-daily-note-card-body is-empty'}>
                <LearningPlanGuideView guide={row.guide} />
              </div>
            </button>
          ))}
        </div>
      </div>
      {visible.length > LEARNING_PLAN_PAGE1_SIZE ? (
        <div className="cp-daily-note-pager" role="navigation" aria-label="활동 페이지">
          <button
            type="button"
            className="cp-daily-note-pager-btn"
            disabled={safePage <= 1}
            aria-label="이전 페이지"
            onClick={() => setPage(safePage - 1)}
          >
            &lt;
          </button>
          <span className="cp-daily-note-pager-status">
            {safePage} / {totalPages}
            <span className="cp-daily-note-pager-count">({visible.length}건)</span>
          </span>
          <button
            type="button"
            className="cp-daily-note-pager-btn"
            disabled={safePage >= totalPages}
            aria-label="다음 페이지"
            onClick={() => setPage(safePage + 1)}
          >
            &gt;
          </button>
        </div>
      ) : null}
      <button type="button" className="cp-fab-save cp-daily-note-fab" onClick={openNew} aria-label="활동 추가" title="활동 추가">
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" aria-hidden>
          <path d="M12 5V19M5 12H19" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
        </svg>
      </button>
      {draft
        ? createPortal(
            <ActivityEditModal
              draft={draft}
              onChange={setDraft}
              onClose={() => setDraft(null)}
              onSave={saveDraft}
              onDelete={removeDraft}
            />,
            document.body
          )
        : null}
      {categoryEditor
        ? createPortal(
            <CategoryNameModal
              title={categoryEditor.id ? '카테고리 이름' : '카테고리 만들기'}
              name={categoryEditor.name}
              error={categoryError}
              onChange={(name) => {
                setCategoryError('');
                setCategoryEditor((prev) => (prev ? { ...prev, name } : prev));
              }}
              onClose={() => {
                setCategoryEditor(null);
                setCategoryError('');
              }}
              onSave={saveCategory}
            />,
            document.body
          )
        : null}
      {pageConfirm
        ? createPortal(
            <AppConfirmModal
              title={pageConfirm.title}
              description={pageConfirm.description}
              confirmLabel={pageConfirm.confirmLabel}
              onClose={() => setPageConfirm(null)}
              onConfirm={() => {
                const run = pageConfirm.run;
                setPageConfirm(null);
                run();
              }}
            />,
            document.body
          )
        : null}
    </div>
  );
};

function ActivityEditModal({
  draft,
  onChange,
  onClose,
  onSave,
  onDelete,
}: {
  draft: ActivityDraft;
  onChange: (next: ActivityDraft) => void;
  onClose: () => void;
  onSave: () => void;
  onDelete: () => void;
}) {
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="cp-modal-backdrop" role="presentation">
      <div
        className="cp-daily-note-editor cp-learning-plan-edit"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cp-learning-plan-edit-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="cp-daily-note-editor-top">
          <input
            id="cp-learning-plan-edit-title"
            className="cp-daily-note-editor-date cp-learning-plan-edit-title"
            value={draft.name}
            autoFocus
            placeholder="활동명"
            aria-label="활동명"
            onChange={(event) => onChange({ ...draft, name: event.target.value })}
          />
          <div className="cp-daily-note-editor-actions">
            {draft.isNew ? null : (
              <DeleteHoldButton
                className="cp-daily-note-editor-trash"
                holdBarClassName="cp-daily-note-editor-trash-bar"
                title="짧게 누르면 확인 후 삭제 · 길게 누르면 바로 삭제"
                ariaLabel="활동 삭제 (짧게: 확인, 길게: 바로 삭제)"
                confirmTitle="이 활동을 삭제할까요?"
                confirmDescription="삭제하면 되돌릴 수 없습니다."
                onDelete={onDelete}
              >
                <img src={`${process.env.PUBLIC_URL || ''}/Trash.svg`} alt="" width={20} height={20} />
              </DeleteHoldButton>
            )}
            <button type="button" className="cp-daily-note-editor-close" onClick={onClose} aria-label="닫기">
              ×
            </button>
          </div>
        </div>
        <input
          className="cp-learning-plan-edit-method"
          value={draft.method}
          placeholder="활동 방법"
          aria-label="활동 방법"
          onChange={(event) => onChange({ ...draft, method: event.target.value })}
        />
        <LearningPlanGuideView
          guide={draft.guide}
          editable
          onChange={(guide) => onChange({ ...draft, guide })}
        />
        <div className="cp-daily-note-editor-footer">
          <button type="button" className="cp-pin-modal-btn cp-pin-modal-btn--secondary" onClick={onClose}>
            닫기
          </button>
          <button type="button" className="cp-pin-modal-btn cp-pin-modal-btn--primary" onClick={onSave}>
            저장
          </button>
        </div>
      </div>
    </div>
  );
}

function CategoryNameModal({
  title,
  name,
  error,
  onChange,
  onClose,
  onSave,
}: {
  title: string;
  name: string;
  error: string;
  onChange: (name: string) => void;
  onClose: () => void;
  onSave: () => void;
}) {
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="cp-modal-backdrop" role="presentation">
      <form
        className="cp-daily-note-editor cp-learning-plan-category-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cp-learning-plan-category-title"
        onMouseDown={(event) => event.stopPropagation()}
        onSubmit={(event) => {
          event.preventDefault();
          onSave();
        }}
      >
        <h3 id="cp-learning-plan-category-title">{title}</h3>
        <input value={name} autoFocus placeholder="예: 도입활동" aria-label="카테고리 이름" onChange={(event) => onChange(event.target.value)} />
        {error ? <p>{error}</p> : null}
        <div className="cp-daily-note-editor-footer">
          <button type="button" className="cp-pin-modal-btn cp-pin-modal-btn--secondary" onClick={onClose}>
            취소
          </button>
          <button type="submit" className="cp-pin-modal-btn cp-pin-modal-btn--primary">
            확인
          </button>
        </div>
      </form>
    </div>
  );
}

export default AdminLearningPlanActivityPage;

function AppConfirmModal({
  title,
  description,
  confirmLabel,
  onClose,
  onConfirm,
}: {
  title: string;
  description: string;
  confirmLabel: string;
  onClose: () => void;
  onConfirm: () => void;
}) {
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      event.stopPropagation();
      onClose();
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [onClose]);

  return (
    <div
      className="cp-pin-confirm-overlay cp-pin-confirm-overlay--viewport cp-delete-hold-confirm-overlay"
      role="presentation"
      onClick={onClose}
      onMouseDown={(event) => event.stopPropagation()}
    >
      <div
        className="cp-pin-confirm cp-pin-confirm--warn"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cp-learning-plan-confirm-title"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="cp-pin-confirm-warn-icon" aria-hidden>
          !
        </div>
        <p id="cp-learning-plan-confirm-title" className="cp-pin-confirm-warn-title">
          {title}
        </p>
        {description ? <p className="cp-pin-confirm-warn-desc">{description}</p> : null}
        <div className="cp-pin-confirm-actions cp-pin-confirm-actions--split">
          <button type="button" className="cp-pin-modal-btn cp-pin-modal-btn--secondary" onClick={onClose}>
            취소
          </button>
          <button type="button" className="cp-pin-modal-btn cp-pin-modal-btn--primary" onClick={onConfirm}>
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
